import { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    PHASE, PHASE_LABEL, PHASE_CONFIG_KEYS, formatTime, formatTimeHms,
} from '@/lib/timer/constants';
import { Play, Pause, Square, Maximize, Minimize } from 'lucide-react';

function phaseColor(phase, colors) {
    switch (phase) {
        case PHASE.PREPARE:
            return colors.prepare;
        case PHASE.WORK:
            return colors.work;
        case PHASE.REST:
            return colors.rest;
        case PHASE.REST_BETWEEN:
            return colors.restBetween;
        case PHASE.COOLDOWN:
            return colors.cooldown;
        case PHASE.END:
        default:
            return colors.tabata;
    }
}

function phaseDuration(phase, config) {
    const key = PHASE_CONFIG_KEYS[phase];
    return key ? config[key] : 0;
}

function ProgressRing({ progress, color, size = 240, stroke = 12 }) {
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - progress * circumference;

    return (
        <svg width={size} height={size} className="transform -rotate-90">
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={stroke}
                fill="transparent"
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke={color}
                strokeWidth={stroke}
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.3s linear' }}
            />
        </svg>
    );
}

export default function TimerRun({ timer, config, onBack }) {
    const containerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const color = phaseColor(timer.phase, config.colors);
    const duration = phaseDuration(timer.phase, config);
    const progress = duration > 0 ? (duration - timer.remaining) / duration : 0;

    useEffect(() => {
        const handleChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(() => {});
        } else {
            document.exitFullscreen().catch(() => {});
        }
    };

    const handleBack = () => {
        timer.stop();
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
        }
        onBack();
    };

    return (
        <div
            ref={containerRef}
            className="min-h-[calc(100vh-6rem)] flex flex-col items-center justify-center rounded-lg p-6 transition-colors duration-500"
            style={{ backgroundColor: color }}
        >
            <div className="flex flex-col items-center text-center text-white w-full max-w-3xl">
                <p className="text-2xl md:text-4xl font-bold uppercase tracking-widest mb-2">
                    {PHASE_LABEL[timer.phase]}
                </p>

                <div className="relative flex items-center justify-center my-6">
                    <ProgressRing progress={progress} color="#ffffff" size={280} stroke={14} />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-6xl md:text-8xl font-bold tabular-nums tracking-tight">
                            {formatTime(timer.remaining)}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-6 md:gap-12 mb-8 text-center">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-white/70">Ronda</p>
                        <p className="text-2xl font-bold">
                            {timer.phase === PHASE.END ? '-' : `${timer.round} / ${timer.roundsTotal}`}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-white/70">Tabata</p>
                        <p className="text-2xl font-bold">
                            {timer.phase === PHASE.END ? '-' : `${timer.tabata} / ${timer.tabatasTotal}`}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-white/70">Restante total</p>
                        <p className="text-2xl font-bold">{formatTimeHms(timer.totalRemaining)}</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3">
                    {timer.phase !== PHASE.END && (
                        <Button
                            size="lg"
                            onClick={timer.running ? timer.pause : timer.resume}
                            className="bg-white text-black hover:bg-white/90 border-0"
                        >
                            {timer.running ? <Pause className="h-5 w-5 mr-2" /> : <Play className="h-5 w-5 mr-2" />}
                            {timer.running ? 'Pausar' : 'Continuar'}
                        </Button>
                    )}
                    <Button
                        size="lg"
                        variant="outline"
                        onClick={handleBack}
                        className="border-white text-white hover:bg-white/20"
                    >
                        <Square className="h-5 w-5 mr-2" />
                        Terminar
                    </Button>
                    <Button
                        size="lg"
                        variant="outline"
                        onClick={toggleFullscreen}
                        className="border-white text-white hover:bg-white/20"
                    >
                        {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                    </Button>
                </div>
            </div>
        </div>
    );
}
